import React from 'react';
import { reportClientError } from '@/utils/errorReporter';

/**
 * App-wide error boundary. Catches any React render crash (the cause of the
 * mobile blank-screen bug), shows a branded fallback instead of a white page,
 * and reports the crash to /api/client-errors.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    reportClientError({
      message: error && error.message,
      stack: error && error.stack,
      source: 'boundary',
      componentStack: errorInfo && errorInfo.componentStack,
    });
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        data-testid="error-boundary-fallback"
        className="min-h-screen flex flex-col"
        style={{ background: 'linear-gradient(135deg, #FED7A5 0%, #73766A 100%)' }}
      >
        {/* Minimal, dependency-free nav so users can still get around / sign in */}
        <nav
          className="w-full flex items-center justify-between px-6 py-4"
          style={{ background: 'rgba(255,255,255,0.92)', boxShadow: '0 2px 10px rgba(32,33,43,0.12)' }}
        >
          <a
            href="/"
            data-testid="error-boundary-nav-logo"
            className="text-2xl"
            style={{ fontFamily: 'Playfair Display, serif', color: '#20212B', fontWeight: 700 }}
          >
            Mementa
          </a>
          <div className="flex items-center gap-4">
            <a href="/browse" className="text-sm font-medium hover:underline" style={{ color: '#534145' }}>
              Browse
            </a>
            <a
              href="/"
              data-testid="error-boundary-nav-signin"
              className="px-5 py-2 rounded-full text-sm font-semibold text-white"
              style={{ background: '#9E6752' }}
            >
              Home / Sign In
            </a>
          </div>
        </nav>

        <div className="flex-1 flex items-center justify-center px-6">
        <div
          className="max-w-md w-full text-center rounded-2xl p-10"
          style={{ background: 'rgba(255,255,255,0.92)', boxShadow: '0 20px 60px rgba(32,33,43,0.25)' }}
        >
          <h1
            className="text-3xl mb-3"
            style={{ fontFamily: 'Playfair Display, serif', color: '#20212B', fontWeight: 700 }}
          >
            Mementa
          </h1>
          <p className="text-base mb-2" style={{ color: '#20212B', fontWeight: 600 }}>
            Something went wrong
          </p>
          <p className="text-sm mb-8" style={{ color: '#73766A' }}>
            We hit an unexpected snag. It has been reported to our team. A quick reload usually fixes it.
          </p>
          <button
            data-testid="error-boundary-reload-button"
            onClick={() => window.location.reload()}
            className="px-8 py-3 rounded-full text-white text-sm font-semibold transition-transform hover:scale-105"
            style={{ background: '#9E6752' }}
          >
            Reload Page
          </button>
        </div>
        </div>
      </div>
    );
  }
}
